import { Flex } from "@chakra-ui/react/flex";
import { Icon } from "@chakra-ui/react/icon";
import {
  AutomationsLink,
  CalendarLink,
  PaymentsLink,
  ReviewsLink,
} from "components/navigation-bar/MobileNavButton";
import {
  useCurrentAgent,
  useUnreadCounts,
} from "hooks/queries/use-current-agent";
import { useMerchant } from "hooks/queries/use-settings-merchant";
import React, { useEffect, useState } from "react";
import {
  LuBot,
  LuBriefcase,
  LuChartArea,
  LuMail,
  LuMegaphone,
  LuSettings,
  LuUserRound,
} from "react-icons/lu";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { isFuzeyMerchant, isIOSMobileApp } from "util/methods";
import {
  canAccessAiChats,
  canManageAutomations,
  canViewAgentsAnalytics,
  canViewPayments,
} from "util/permissions";
import FooterIcon from "./FooterIcon";
import FooterNavLink from "./FooterNavLink";
import FooterNestedNavLinks from "./FooterNestedNavLinks";

const Footer = () => {
  const { data: merchant } = useMerchant();
  const { data: currentAgent } = useCurrentAgent();
  const { data: unreadCounts = [] } = useUnreadCounts();
  const unreadCount = unreadCounts.find((uc) => uc.merchant_id === merchant.id);
  const { pathname } = useLocation();
  const { activeConversationId } = useAppSelector(
    (state) => state.conversations
  );
  const { colorScheme } = useAppSelector((state) => state.theme);

  const [isCustomerManagementActive, setIsCustomerManagementActive] =
    useState<boolean>(false);

  useEffect(() => {
    setIsCustomerManagementActive(
      pathname.includes("calendar") ||
        pathname.includes("payments") ||
        pathname.includes("reviews")
    );
  }, [pathname]);

  if (activeConversationId) {
    return null;
  }

  return (
    <Flex
      display="flex"
      justifyContent="space-between"
      alignItems="start"
      width="100%"
      position="sticky"
      bgColor={`${colorScheme}.900`}
      px={4}
      pt={5}
      pb={isIOSMobileApp() ? 12 : 5}
    >
      <FooterNavLink
        to={`/${merchant.id}/inbox`}
        hasNotifications={!!unreadCount?.personal || !!unreadCount?.shared}
      >
        <FooterIcon>
          <LuMail />
        </FooterIcon>
      </FooterNavLink>

      {canAccessAiChats(currentAgent) ? (
        <FooterNavLink to={`/${merchant.id}/ai-chats`}>
          <FooterIcon w={7} h={7}>
            <LuBot />
          </FooterIcon>
        </FooterNavLink>
      ) : null}

      <FooterNavLink to={`/${merchant.id}/contacts`}>
        <FooterIcon w={7} h={7}>
          <Icon as={LuUserRound} />
        </FooterIcon>
      </FooterNavLink>

      <FooterNavLink to={`/${merchant.id}/campaigns`}>
        <FooterIcon w={7} h={7}>
          <LuMegaphone />
        </FooterIcon>
      </FooterNavLink>

      <FooterNestedNavLinks
        mainIcon={
          <FooterIcon
            fill={isCustomerManagementActive ? `${colorScheme}.400` : "white"}
          >
            <LuBriefcase />
          </FooterIcon>
        }
        hasNotifications={false}
      >
        <CalendarLink />
        {!canViewPayments(merchant.id, currentAgent!) ? null : <PaymentsLink />}
        {!canManageAutomations(merchant.id, currentAgent!) ? null : (
          <AutomationsLink />
        )}
        <ReviewsLink />
      </FooterNestedNavLinks>

      {canViewAgentsAnalytics(merchant.id, currentAgent!) ? (
        <FooterNavLink to={`/${merchant.id}/analytics`}>
          <FooterIcon w={7} h={7}>
            <LuChartArea />
          </FooterIcon>
        </FooterNavLink>
      ) : null}

      <FooterNavLink to={`/${merchant.id}/settings`}>
        <FooterIcon w={7} h={7}>
          <LuSettings />
        </FooterIcon>
      </FooterNavLink>
    </Flex>
  );
};

export default Footer;
